<template>
  <Admin style="background: #f2f2f2">
    <v-main>
      <v-container fluid>
        <v-card class="rounded-xl tw-min-h-80">
          <v-card-text>
            <v-breadcrumbs :items="breadcrumbItems">
              <template v-slot:divider>
                <v-icon>mdi-chevron-right</v-icon>
              </template>
              <template v-slot:item="{ item }">
                <v-breadcrumbs-item
                  :to="item.to"
                  :disabled="item.disabled"
                  :exact="true"
                >
                  {{ item.text }}
                </v-breadcrumbs-item>
              </template>
            </v-breadcrumbs>
            <div
              class="tw-mx-5 tw-font-bold tw-text-xl tw-text-black tw-tracking-wide"
            >
              ตั้งค่า API
            </div>

            <!-- ── API Key + Public Key (legacy) ─────────────────────────── -->
            <section class="tw-mx-5 tw-mt-6">
              <div class="tw-flex tw-justify-between tw-items-start">
                <div>
                  <h3 class="tw-text-base tw-font-bold tw-text-black">
                    API Key และ Public Key
                  </h3>
                  <div class="tw-text-sm tw-text-gray-600 tw-mt-1">
                    คีย์ของผู้ใช้แต่ละคน สำหรับระบบที่เชื่อมต่อผ่าน API แบบเดิม
                  </div>
                  <div v-if="apiKeyProducts.length" class="tw-mt-2">
                    <span class="tw-text-xs tw-text-gray-500 tw-mr-1">ใช้กับ</span>
                    <v-chip
                      v-for="p in apiKeyProducts"
                      :key="p"
                      x-small
                      class="mr-1"
                    >
                      {{ p }}
                    </v-chip>
                  </div>
                  <div v-if="showApiTokens" class="tw-text-xs tw-text-gray-500 tw-mt-1">
                    Lao TaxRIS ยังรับ API Key นี้ได้ แต่แนะนำให้ใช้ API Tokens
                    ด้านล่าง ซึ่งกำหนดสิทธิ์และเพิกถอนได้
                  </div>
                </div>
                <v-btn
                  color="primary"
                  large
                  outlined
                  @click="startEditing"
                  v-if="!isEditing && !isLoading"
                >
                  <v-icon left>mdi-pencil</v-icon>
                  แก้ไข
                </v-btn>
              </div>
              <div
                v-if="isLoading"
                class="tw-flex tw-items-center tw-justify-center tw-py-16"
              >
                <v-progress-circular
                  color="primary"
                  indeterminate
                ></v-progress-circular>
              </div>
              <v-form v-else>
                <v-row>
                  <v-col cols="12">
                    <v-text-field
                      v-model="settingAPI.apiKey"
                      class="tw-m-0 tw-pt-1"
                      dense
                      label="API Key"
                      outlined
                      :readonly="!isEditing"
                      :type="showApiKey ? 'text' : 'password'"
                    >
                      <template v-slot:append>
                        <v-btn
                          icon
                          :aria-label="showApiKey ? 'ซ่อน API Key' : 'แสดง API Key'"
                          :title="showApiKey ? 'ซ่อน API Key' : 'แสดง API Key'"
                          @click="toggleApiKeyVisibility"
                        >
                          <v-icon>{{ showApiKey ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
                        </v-btn>
                        <v-btn
                          icon
                          aria-label="คัดลอก API Key"
                          title="คัดลอก API Key"
                          @click="copyText(settingAPI.apiKey, 'API Key')"
                        >
                          <v-icon>mdi-content-copy</v-icon>
                        </v-btn>
                      </template>
                    </v-text-field>
                    <div
                      v-if="apiKeyChanged"
                      class="tw-mt-1 tw-text-sm tw-text-orange-700"
                    >
                      <v-icon small color="orange darken-2">mdi-alert</v-icon>
                      API Key ใหม่ยังไม่ถูกบันทึก — เมื่อกดบันทึก คีย์เดิมจะใช้ไม่ได้ทันที
                    </div>
                    <div v-if="isEditing" class="tw-mt-1">
                      <v-btn
                        color="secondary"
                        outlined
                        @click="generateNewApiKey"
                        small
                      >
                        <v-icon left>mdi-refresh</v-icon>
                        สร้าง API Key ใหม่
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12">
                    <div class="tw-font-bold tw-text-sm tw-text-black">
                      Company Public Key
                    </div>
                    <div class="tw-text-sm tw-text-gray-600 tw-mb-3">
                      ใช้เข้ารหัสรหัสผ่าน SAP Business One ที่ตั้งในหน้าโปรไฟล์
                      — ใช้ร่วมกันทั้งบริษัท ถ้าเปลี่ยนคีย์
                      ผู้ใช้ทุกคนต้องบันทึกรหัสผ่าน SAP B1 ใหม่
                    </div>
                    <v-textarea
                      v-model="settingAPI.publicKey"
                      class="tw-m-0 tw-pt-1"
                      dense
                      label="Company Public Key (RSA)"
                      outlined
                      :readonly="!isEditing"
                      :rows="3"
                      placeholder="-----BEGIN PUBLIC KEY-----&#10;MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEA...&#10;-----END PUBLIC KEY-----"
                      auto-grow
                    >
                      <template v-slot:append>
                        <v-btn
                          icon
                          aria-label="คัดลอก Public Key"
                          title="คัดลอก Public Key"
                          @click="copyText(settingAPI.publicKey, 'Public Key')"
                        >
                          <v-icon>mdi-content-copy</v-icon>
                        </v-btn>
                      </template>
                    </v-textarea>
                  </v-col>
                </v-row>
                <div v-if="isEditing" class="tw-flex tw-justify-end">
                  <v-btn
                    class="mr-2"
                    color="primary"
                    large
                    outlined
                    :disabled="isSaving"
                    @click="cancelEditing"
                  >
                    <v-icon left>mdi-close</v-icon>
                    ยกเลิก
                  </v-btn>
                  <v-btn
                    :loading="isSaving"
                    color="primary"
                    large
                    @click="save"
                  >
                    <v-icon left>mdi-content-save</v-icon>
                    บันทึก
                  </v-btn>
                </div>
              </v-form>
            </section>

            <v-divider v-if="showApiTokens" class="tw-mx-5 tw-my-8" />

            <!-- ── API Tokens (v2) — Lao TaxRIS only: apiLaoEtaxV1 is the
                 sole endpoint that accepts these tokens. ────────────────── -->
            <section v-if="showApiTokens" class="tw-mx-5">
              <div class="tw-flex tw-justify-between tw-items-start">
                <div>
                  <h3 class="tw-text-base tw-font-bold tw-text-black">
                    API Tokens
                  </h3>
                  <div class="tw-text-sm tw-text-gray-600 tw-mt-1">
                    โทเคนสำหรับให้ระบบ ERP ภายนอกส่งเอกสารเข้ามา (Lao TaxRIS)
                    — ผูกกับบริษัทนี้ กำหนดสิทธิ์และเพิกถอนได้
                  </div>
                  <a
                    :href="apiDocsUrl"
                    target="_blank"
                    rel="noopener"
                    class="tw-text-sm tw-inline-flex tw-items-center tw-mt-2"
                  >
                    <v-icon small color="primary" class="tw-mr-1">mdi-book-open-variant</v-icon>
                    คู่มือเชื่อมต่อ + format ที่ต้องส่ง
                    <v-icon x-small color="primary" class="tw-ml-1">mdi-open-in-new</v-icon>
                  </a>
                </div>
                <v-btn
                  v-if="!tokensForbidden"
                  color="primary"
                  large
                  outlined
                  @click="openCreateToken"
                >
                  <v-icon left>mdi-key-plus</v-icon>
                  สร้าง Token
                </v-btn>
              </div>

              <div class="tw-mt-4">
                <div v-if="tokensForbidden" class="tw-text-sm tw-text-gray-500 tw-py-4">
                  <v-icon small>mdi-lock</v-icon>
                  เฉพาะผู้ดูแล (admin) ของบริษัทเท่านั้นที่ดูและจัดการ token ได้
                </div>
                <v-simple-table v-else-if="sortedTokens.length">
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left">ชื่อ</th>
                        <th class="text-left">โทเคน</th>
                        <th class="text-left">สิทธิ์</th>
                        <th class="text-left">หมดอายุ</th>
                        <th class="text-left">ใช้ล่าสุด</th>
                        <th class="text-left">สถานะ</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="t in sortedTokens" :key="t.tokenId">
                        <td>{{ t.label }}</td>
                        <td><code class="tw-text-xs">{{ t.masked }}</code></td>
                        <td>
                          <v-chip
                            v-for="s in t.scopes || []"
                            :key="s"
                            x-small
                            class="mr-1"
                            :title="s"
                          >
                            {{ scopeLabels[s] || s }}
                          </v-chip>
                        </td>
                        <td class="tw-text-xs">{{ formatTokenDate(t.expiresAt) || 'ไม่หมดอายุ' }}</td>
                        <td class="tw-text-xs">{{ formatTokenDate(t.lastUsedAt) || 'ยังไม่เคยใช้' }}</td>
                        <td>
                          <v-chip small :color="tokenStatus(t).color" text-color="white">
                            <v-icon left small>{{ tokenStatus(t).icon }}</v-icon>
                            {{ tokenStatus(t).text }}
                          </v-chip>
                        </td>
                        <td class="text-right">
                          <v-btn
                            v-if="!t.revokedAt"
                            icon
                            color="error"
                            :aria-label="`เพิกถอน token ${t.label}`"
                            title="เพิกถอน token"
                            :loading="revokingTokenId === t.tokenId"
                            @click="confirmRevokeToken(t)"
                          >
                            <v-icon>mdi-cancel</v-icon>
                          </v-btn>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
                <div v-else-if="!isLoadingTokens" class="tw-text-sm tw-text-gray-500 tw-py-4">
                  ยังไม่มี token — กด "สร้าง Token" เพื่อให้ ERP เชื่อมต่อเข้ามาได้
                </div>
                <v-progress-linear v-if="isLoadingTokens" indeterminate color="primary" />
              </div>
            </section>
          </v-card-text>
        </v-card>

        <!-- create -->
        <v-dialog v-model="createTokenDialog" max-width="560" persistent>
          <v-card>
            <v-card-title>สร้าง API Token</v-card-title>
            <v-card-text>
              <v-text-field
                v-model="newTokenLabel"
                label="ชื่อเรียก (เช่น SAP B1 production)"
                outlined
                dense
                :rules="[(v) => !!v || 'ต้องระบุชื่อ']"
              />
              <v-select
                v-model="newTokenScopes"
                :items="availableScopes"
                label="สิทธิ์"
                multiple
                chips
                outlined
                dense
              />
              <v-text-field
                v-model="newTokenExpiresInDays"
                label="หมดอายุใน (วัน)"
                type="number"
                min="1"
                max="3650"
                outlined
                dense
                persistent-hint
                :hint="newTokenExpiryHint"
                :rules="[expiryRule]"
              />
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn text @click="createTokenDialog = false">ยกเลิก</v-btn>
              <v-btn
                color="primary"
                :loading="isCreatingToken"
                :disabled="!newTokenLabel || !newTokenScopes.length || expiryRule(newTokenExpiresInDays) !== true"
                @click="createToken"
              >
                สร้าง
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- shown once -->
        <v-dialog v-model="mintedTokenDialog" max-width="640" persistent>
          <v-card>
            <v-card-title class="tw-text-orange-700">คัดลอก token นี้ทันที</v-card-title>
            <v-card-text>
              <v-alert type="warning" dense text>
                ระบบเก็บเฉพาะค่า hash ของ token นี้ — ปิดหน้าต่างนี้แล้วจะดูอีกไม่ได้
                ถ้าทำหาย ต้องเพิกถอนแล้วสร้างใหม่
              </v-alert>
              <v-textarea
                :value="mintedToken"
                readonly
                outlined
                rows="3"
                class="tw-mt-2 minted-token"
                @focus="$event.target.select()"
              >
                <template v-slot:append>
                  <v-btn
                    icon
                    aria-label="คัดลอก token"
                    title="คัดลอก token"
                    @click="copyMintedToken"
                  >
                    <v-icon>mdi-content-copy</v-icon>
                  </v-btn>
                </template>
              </v-textarea>
              <div class="tw-text-sm tw-text-gray-600">
                ส่ง token นี้ให้ทีม dev พร้อมลิงก์คู่มือ:
                <a :href="apiDocsUrl" target="_blank" rel="noopener">{{ apiDocsUrl }}</a>
              </div>
            </v-card-text>
            <v-card-actions>
              <v-btn text @click="closeMintedTokenWithoutCopy">ปิดโดยไม่คัดลอก</v-btn>
              <v-spacer />
              <v-btn
                color="primary"
                :disabled="!mintedTokenCopied"
                @click="closeMintedToken"
              >
                คัดลอกแล้ว ปิดเลย
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-container>
    </v-main>
  </Admin>
</template>

<script>
import Admin from "../../components/admin/admin-main-template";
import fc from "@/firebaseConfig";
import { mapState } from "vuex";

const SCOPE_LABELS = {
  "lao.docs.write": "ส่งเอกสาร",
  "lao.docs.read": "อ่านสถานะ",
  "lao.pdf.read": "ดาวน์โหลด PDF",
};

export default {
  name: "SettingAPI",
  components: {
    Admin,
  },
  data: () => ({
    breadcrumbItems: [
      { text: "ตั้งค่า", disabled: false, to: { name: "Company.Settings" } },
      { text: "API", disabled: true },
    ],
    isLoading: false,
    isSaving: false,
    isEditing: false,
    showApiKey: false,
    // What is in the form. Outside edit mode it mirrors `savedSettingAPI`.
    settingAPI: {
      apiKey: "",
      publicKey: "",
    },
    // What is in Firestore — cancel restores from here.
    savedSettingAPI: {
      apiKey: "",
      publicKey: "",
    },
    userUnsubscribe: null,
    companyUnsubscribe: null,

    // API Tokens (v2)
    apiTokens: [],
    isLoadingTokens: false,
    tokensForbidden: false,
    createTokenDialog: false,
    isCreatingToken: false,
    newTokenLabel: "",
    newTokenScopes: ["lao.docs.write", "lao.docs.read", "lao.pdf.read"],
    newTokenExpiresInDays: "",
    mintedToken: "",
    mintedTokenDialog: false,
    mintedTokenCopied: false,
    revokingTokenId: "",
    scopeLabels: SCOPE_LABELS,
    availableScopes: Object.keys(SCOPE_LABELS).map((value) => ({
      text: `${SCOPE_LABELS[value]} (${value})`,
      value,
    })),
  }),
  computed: {
    ...mapState({
      productList: (state) => state.companies.selectedCompanyProductList || {},
    }),
    showApiTokens() {
      return !!this.productList.isLaoTaxRis;
    },
    // Products whose endpoints authenticate with the legacy apiKey:
    // apiDocsSet/Get/Del + apiKBankQrPayment (Thai eTax),
    // apiFixedAssetNetsuiteCount*, and apiLaoEtaxV1's legacy path.
    apiKeyProducts() {
      const p = this.productList;
      const out = [];
      if (p.isEtax) out.push("eTax ไทย", "KBank QR");
      if (p.isFixedAssetCount) out.push("Fixed Asset Count");
      if (p.isLaoTaxRis) out.push("Lao TaxRIS");
      return out;
    },
    // ผูกกับ origin ที่กำลังเปิดอยู่ ไม่ hardcode phasicharoen.com — บน
    // sld-etax-staging ลิงก์จะชี้ไป staging เอง
    apiDocsUrl() {
      return `${window.location.origin}/api/v1/lao/schema`;
    },
    apiKeyChanged() {
      return (
        this.isEditing &&
        this.settingAPI.apiKey !== this.savedSettingAPI.apiKey
      );
    },
    // The list endpoint has no orderBy, so Firestore's order is arbitrary.
    sortedTokens() {
      return this.apiTokens
        .slice()
        .sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""));
    },
    newTokenExpiryHint() {
      const days = Number(this.newTokenExpiresInDays);
      if (!this.newTokenExpiresInDays || this.expiryRule(days) !== true) {
        return "เว้นว่าง = ไม่หมดอายุ";
      }
      const at = new Date(Date.now() + days * 24 * 60 * 60 * 1000);
      return `หมดอายุ ${this.formatTokenDate(at.toISOString())}`;
    },
  },
  methods: {
    async save() {
      this.isSaving = true;
      try {
        const userId = fc.auth.currentUser.uid;
        const companyId = this.$router.currentRoute.params.companyId;

        // One batch, so a failure can't leave the API key saved but the
        // public key not (or the other way round).
        const batch = fc.db.batch();
        // API Key → CompaniesUsers (user-specific)
        batch.set(
          fc.db.doc(`/CompaniesUsers/${companyId}_${userId}`),
          { apiKey: this.settingAPI.apiKey },
          { merge: true }
        );
        // Public Key → Companies (company-wide)
        batch.set(
          fc.db.doc(`/Companies/${companyId}`),
          { publicKey: this.settingAPI.publicKey },
          { merge: true }
        );
        await batch.commit();

        this.savedSettingAPI = { ...this.settingAPI };
        this.isEditing = false;
        this.$message.success("บันทึกแล้ว");
      } catch (e) {
        // Stay in edit mode so the user's input isn't lost.
        this.$message.error(`บันทึกไม่สำเร็จ: ${e.message}`);
      } finally {
        this.isSaving = false;
      }
    },
    listenToAPISetting() {
      const companyId = this.$router.currentRoute.params.companyId;
      const userId = fc.auth.currentUser.uid;

      this.isLoading = true;

      // API Key from CompaniesUsers
      this.userUnsubscribe = fc.db
        .doc(`/CompaniesUsers/${companyId}_${userId}`)
        .onSnapshot(
          (snapshot) => {
            const data = snapshot.exists ? snapshot.data() : null;
            this.applySavedValue("apiKey", (data && data.apiKey) || "");
          },
          () => {
            this.$message.error("โหลด API Key ไม่สำเร็จ");
          }
        );

      // Public Key from Companies
      this.companyUnsubscribe = fc.db.doc(`/Companies/${companyId}`).onSnapshot(
        (snapshot) => {
          const data = snapshot.exists ? snapshot.data() : null;
          this.applySavedValue("publicKey", (data && data.publicKey) || "");
          this.isLoading = false;
        },
        () => {
          this.$message.error("โหลด Public Key ไม่สำเร็จ");
          this.isLoading = false;
        }
      );
    },
    // The Companies doc changes for many unrelated reasons; while the user is
    // editing, a snapshot must not overwrite what they are typing.
    applySavedValue(field, value) {
      this.savedSettingAPI[field] = value;
      if (!this.isEditing) {
        this.settingAPI[field] = value;
      }
    },
    startEditing() {
      this.isEditing = true;
    },
    cancelEditing() {
      this.settingAPI = { ...this.savedSettingAPI };
      this.isEditing = false;
    },
    // API keys authenticate apiDocsSet, which writes tax documents. They must be
    // unguessable, so they come from the platform CSPRNG — never Math.random(),
    // whose internal state is recoverable from a handful of outputs, making every
    // key ever generated by this page predictable from any one of them.
    //
    // Rejection sampling (not `% ALPHABET.length`) keeps the distribution flat:
    // 256 is not a multiple of 62, so plain modulo would favour the first
    // 256 % 62 === 8 characters of the alphabet.
    generateRandomApiKey() {
      const LENGTH = 128;
      const ALPHABET =
        'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
      const LIMIT = 256 - (256 % ALPHABET.length); // 248

      const csprng = window.crypto || window.msCrypto;
      if (!csprng || !csprng.getRandomValues) {
        throw new Error(
          'เบราว์เซอร์นี้ไม่รองรับการสร้างคีย์อย่างปลอดภัย กรุณาใช้เบราว์เซอร์รุ่นใหม่'
        );
      }

      let apiKey = '';
      const buf = new Uint8Array(LENGTH);
      while (apiKey.length < LENGTH) {
        csprng.getRandomValues(buf);
        for (let i = 0; i < buf.length && apiKey.length < LENGTH; i++) {
          if (buf[i] < LIMIT) {
            apiKey += ALPHABET.charAt(buf[i] % ALPHABET.length);
          }
        }
      }
      return apiKey;
    },
    // ── API Tokens (v2) ─────────────────────────────────────────────────
    // Minting happens server-side (apiIssueCompanyApiToken). The browser must
    // never generate a credential: it cannot guarantee a CSPRNG, which is how
    // the legacy apiKey above ended up predictable.
    tokenApiUrl(suffix) {
      const base =
        process.env.VUE_APP_FIREBASE_FUNCTIONS_URL ||
        "https://us-central1-sld-etax.cloudfunctions.net";
      return `${base}/apiIssueCompanyApiToken${suffix || ""}`;
    },

    // Returns { status, data }. Throws with the server's message on failure,
    // except 403, which callers handle as "not a company admin".
    async tokenRequest(suffix, options) {
      const idToken = await fc.auth.currentUser.getIdToken();
      const res = await fetch(this.tokenApiUrl(suffix), {
        ...options,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
      });
      // A gateway error page is HTML, not JSON — don't surface a parse error.
      const json = await res.json().catch(() => ({}));
      if (res.status === 403) return { status: 403, data: null };
      if (!res.ok || !json.success) {
        throw new Error(json.error || `HTTP ${res.status}`);
      }
      return { status: res.status, data: json.data };
    },

    formatTokenDate(iso) {
      if (!iso) return "";
      return new Date(iso).toLocaleString("th-TH", { calendar: "gregory" });
    },

    tokenStatus(t) {
      if (t.revokedAt) {
        return { text: "เพิกถอนแล้ว", color: "grey", icon: "mdi-cancel" };
      }
      if (t.expiresAt && new Date(t.expiresAt) <= new Date()) {
        return { text: "หมดอายุ", color: "orange darken-2", icon: "mdi-clock-alert-outline" };
      }
      return { text: "ใช้งานได้", color: "green", icon: "mdi-check-circle" };
    },

    expiryRule(v) {
      if (v === "" || v === null || v === undefined) return true;
      const n = Number(v);
      return (Number.isInteger(n) && n >= 1 && n <= 3650) || "ต้องเป็นจำนวนเต็ม 1–3650 วัน";
    },

    async loadApiTokens() {
      const companyId = this.$router.currentRoute.params.companyId;
      if (!companyId) return;
      this.isLoadingTokens = true;
      try {
        const { status, data } = await this.tokenRequest(
          `/list?companyId=${encodeURIComponent(companyId)}`,
          { method: "GET" }
        );
        this.tokensForbidden = status === 403;
        this.apiTokens = (data && data.tokens) || [];
      } catch (e) {
        // A listing failure must not blank the page — the legacy API key card
        // above still has to work.
        this.$message.error(`โหลดรายการ token ไม่สำเร็จ: ${e.message}`);
      } finally {
        this.isLoadingTokens = false;
      }
    },

    openCreateToken() {
      this.newTokenLabel = "";
      this.newTokenScopes = ["lao.docs.write", "lao.docs.read", "lao.pdf.read"];
      this.newTokenExpiresInDays = "";
      this.createTokenDialog = true;
    },

    async createToken() {
      const companyId = this.$router.currentRoute.params.companyId;
      this.isCreatingToken = true;
      try {
        const body = {
          companyId,
          label: this.newTokenLabel,
          scopes: this.newTokenScopes,
        };
        if (this.newTokenExpiresInDays) {
          body.expiresInDays = Number(this.newTokenExpiresInDays);
        }
        const { status, data } = await this.tokenRequest("", {
          method: "POST",
          body: JSON.stringify(body),
        });
        if (status === 403) {
          throw new Error("เฉพาะผู้ดูแล (admin) ของบริษัทเท่านั้นที่สร้าง token ได้");
        }
        this.createTokenDialog = false;
        // Shown once — the server keeps only the hash.
        this.mintedToken = data.token;
        this.mintedTokenCopied = false;
        this.mintedTokenDialog = true;
        await this.loadApiTokens();
      } catch (e) {
        this.$message.error(`สร้าง token ไม่สำเร็จ: ${e.message}`);
      } finally {
        this.isCreatingToken = false;
      }
    },

    async copyText(text, label) {
      try {
        await navigator.clipboard.writeText(text);
        this.$message.success(`คัดลอก ${label} แล้ว`);
        return true;
      } catch (e) {
        // navigator.clipboard is undefined outside a secure context.
        this.$message.error(`คัดลอก ${label} ไม่สำเร็จ — กรุณาเลือกข้อความแล้วคัดลอกเอง`);
        return false;
      }
    },

    async copyMintedToken() {
      if (await this.copyText(this.mintedToken, "token")) {
        this.mintedTokenCopied = true;
      }
    },

    async closeMintedTokenWithoutCopy() {
      try {
        await this.$confirm(
          "token นี้จะไม่แสดงอีก ถ้ายังไม่ได้เก็บไว้ ต้องเพิกถอนแล้วสร้างใหม่",
          "ปิดโดยไม่คัดลอก?",
          { confirmButtonText: "ปิด", cancelButtonText: "ยกเลิก", type: "warning" }
        );
      } catch (e) {
        return;
      }
      this.closeMintedToken();
    },

    closeMintedToken() {
      this.mintedTokenDialog = false;
      this.mintedToken = "";
      this.mintedTokenCopied = false;
    },

    async confirmRevokeToken(token) {
      // Revoking is outward-facing and immediate: any ERP using this token stops
      // working the moment it lands.
      try {
        await this.$confirm(
          `ระบบ ERP ที่ใช้ token "${token.label}" อยู่จะส่งเอกสารไม่ได้ทันที และย้อนกลับไม่ได้`,
          "เพิกถอน token?",
          { confirmButtonText: "เพิกถอน", cancelButtonText: "ยกเลิก", type: "warning" }
        );
      } catch (e) {
        return;
      }

      const companyId = this.$router.currentRoute.params.companyId;
      this.revokingTokenId = token.tokenId;
      try {
        const { status } = await this.tokenRequest("/revoke", {
          method: "POST",
          body: JSON.stringify({ companyId, tokenId: token.tokenId }),
        });
        if (status === 403) {
          throw new Error("เฉพาะผู้ดูแล (admin) ของบริษัทเท่านั้นที่เพิกถอน token ได้");
        }
        this.$message.success("เพิกถอน token แล้ว");
        await this.loadApiTokens();
      } catch (e) {
        this.$message.error(`เพิกถอนไม่สำเร็จ: ${e.message}`);
      } finally {
        this.revokingTokenId = "";
      }
    },

    async generateNewApiKey() {
      // Replacing a live key breaks whatever integration uses it, as soon as
      // the user presses save.
      if (this.savedSettingAPI.apiKey) {
        try {
          await this.$confirm(
            "เมื่อกดบันทึก API Key เดิมจะใช้ไม่ได้ทันที ระบบที่ใช้คีย์เดิมอยู่จะเชื่อมต่อไม่ได้จนกว่าจะเปลี่ยนเป็นคีย์ใหม่",
            "สร้าง API Key ใหม่?",
            { confirmButtonText: "สร้างคีย์ใหม่", cancelButtonText: "ยกเลิก", type: "warning" }
          );
        } catch (e) {
          return;
        }
      }
      try {
        this.settingAPI.apiKey = this.generateRandomApiKey();
        this.showApiKey = true;
      } catch (e) {
        // Leave the existing key in place — a half-generated or fallback key is
        // worse than no change, because the user would save it believing it safe.
        this.$message.error(e.message);
      }
    },
    toggleApiKeyVisibility() {
      this.showApiKey = !this.showApiKey;
    },
  },
  async mounted() {
    this.listenToAPISetting();
    // ProductList decides which sections apply. Read it here rather than trust
    // the store: a deep link to this page arrives before anything else loads it.
    await this.$store.dispatch("companies/getCompanyProductList", {
      companyId: this.$route.params.companyId,
    });
    if (this.showApiTokens) {
      this.loadApiTokens();
    }
  },
  beforeDestroy() {
    if (this.userUnsubscribe) {
      this.userUnsubscribe();
    }
    if (this.companyUnsubscribe) {
      this.companyUnsubscribe();
    }
  },
};
</script>

<style lang="scss" scoped>
// Hide empty validation message space on this page's inline fields. Scoped:
// unscoped, these rules leaked to every page loaded after this one.
::v-deep .v-text-field__details,
::v-deep .v-textarea__details {
  min-height: 0 !important;
  padding: 0 !important;
}

::v-deep .v-messages {
  min-height: 0 !important;
}

.minted-token ::v-deep textarea {
  font-family: monospace;
  word-break: break-all;
}
</style>
